/**
 * Ayuda — Ventas → Clientes
 */

/** @type {import('../help-index.js').HelpSidebarItem} */
export const ADM_SAL_ENTITIES_ITEM = {
  id: 'entities',
  screenId: 'adm-sal-entities',
  label: 'Clientes',
  description: 'Ver, buscar y cargar clientes.',
  detail: {
    sections: [
      {
        title: 'Paso 1 — Ver el listado de clientes',
        intro:
          'Cuando elegís Clientes en el menú de la izquierda, aparece una lista con todos los clientes cargados. Cada fila muestra:',
        bullets: [
          'Nombre: el nombre legal del cliente (razón social).',
          'Nombre fantasía: el nombre comercial, si usa uno distinto.',
          'Tipo ident.: el tipo de documento (CUIT, DNI, etc.).',
          'N° identif.: el número de ese documento.',
          'Un ícono de lápiz para modificar los datos.',
        ],
        paragraphs: [
          'Para ver los datos de un cliente sin modificar nada, hacé clic en la fila (en cualquier parte que no sea el lápiz). Se abre una ventana con su información.',
          'Para cambiar los datos, hacé clic en el ícono del lápiz.',
          'Si hay muchos clientes, podés pasar de página con los controles de abajo de la lista. También podés descargar la información.',
        ],
      },
      {
        title: 'Paso 2 — Buscar un cliente',
        intro: 'Arriba de la lista hay un campo que dice «Buscar Clientes».',
        bullets: [
          'Escribí al menos 3 letras del nombre para que el sistema busque.',
          'Si seguís escribiendo, la lista se va achicando entre los resultados encontrados.',
          'Si borrás lo que escribiste, vuelve a mostrarse la lista completa.',
        ],
      },
      {
        title: 'Paso 3 — Cargar un cliente nuevo',
        intro:
          'El botón verde con el signo + (arriba a la derecha) abre la pantalla «Agregar nuevo». Completá los datos y, al final, tocá «Crear entidad». Si todo está bien, la ventana se cierra sola y el cliente aparece en la lista.',
      },
      {
        title: 'Datos principales del cliente nuevo',
        intro: 'Son los datos básicos de identificación e impuestos.',
        fields: [
          {
            label: 'Tipo de identificación',
            description:
              'Indicá qué documento tiene el cliente: CUIT, CUIL, DNI, etc. Si cargás CUIT o DNI y completás el número, el sistema puede buscar los datos en AFIP y completar automáticamente el nombre y el domicilio.',
          },
          {
            label: 'Número de identificación',
            description:
              'El número del documento, solo con dígitos. El CUIT tiene 11 números; el DNI, 8. Cuando completás la cantidad correcta, el sistema intenta traer los datos de AFIP.',
          },
          {
            label: 'Razón social',
            description:
              'El nombre legal del cliente. Es obligatorio. Si los datos vinieron de AFIP, este campo queda fijo hasta que cambies el tipo o número de documento.',
          },
          {
            label: 'Nombre de fantasía',
            description:
              'El nombre con el que lo conocés en el día a día. No es obligatorio, pero ayuda a encontrarlo en la lista.',
          },
          {
            label: 'Condición frente al IVA',
            description:
              'Cómo está inscripto ante el IVA: Responsable Inscripto, Monotributo, Consumidor Final, etc. Esto define qué tipo de factura le podés hacer.',
          },
          {
            label: 'Inscripción IIBB',
            description:
              'Número de inscripción en Ingresos Brutos, si corresponde en tu provincia.',
          },
          {
            label: 'Cliente / Proveedor',
            description:
              'Indicá si esta persona es cliente, proveedor o las dos cosas. Desde Ventas, «Cliente» ya viene marcado. Dejalo así salvo que también sea proveedor tuyo.',
          },
        ],
      },
      {
        title: 'Direcciones',
        intro:
          'Podés cargar una o más direcciones. Usá «Agregar dirección» para sumar otra, o «Quitar» para borrar la que no necesites.',
        fields: [
          {
            label: 'Descripción',
            description: 'Para qué sirve esa dirección. Por ejemplo: Fiscal, Entrega, Depósito.',
          },
          {
            label: 'Calle y número',
            description: 'La calle, el número, piso y departamento.',
          },
          {
            label: 'Localidad',
            description: 'La ciudad o localidad.',
          },
          {
            label: 'Provincia (texto)',
            description: 'La provincia, escrita a mano en este bloque de dirección.',
          },
          {
            label: 'CP',
            description: 'El código postal.',
          },
          {
            label: 'País (texto)',
            description: 'El país, escrito en este bloque de dirección.',
          },
        ],
        paragraphs: [
          'Si los datos vinieron de AFIP, la primera dirección puede quedar completa y no dejarte cambiarla hasta que modifiques el documento.',
        ],
      },
      {
        title: 'Teléfonos y correos',
        intro:
          'Podés cargar varios teléfonos y varios correos. En cada uno indicá para qué sirve (por ejemplo «Administración» o «Cobranzas») y el número o email.',
        fields: [
          {
            label: 'Descripción',
            description: 'Un nombre para identificar ese teléfono o correo.',
          },
          {
            label: 'Número / Correo',
            description: 'El teléfono o la dirección de email. Solo se guardan los que tengan algo escrito.',
          },
        ],
      },
      {
        title: 'Ubicación fiscal',
        fields: [
          {
            label: 'País',
            description: 'El país donde está inscripto fiscalmente. Si elegís Argentina, aparece el campo Provincia.',
          },
          {
            label: 'Provincia',
            description: 'La provincia argentina, elegida de la lista. Solo se ve si el país es Argentina.',
          },
        ],
      },
      {
        title: 'Lista de precios',
        fields: [
          {
            label: 'Lista de precios',
            description:
              'Qué lista de precios le corresponde a este cliente cuando le vendés. Si no usa una lista especial, dejalo en «Sin lista».',
          },
        ],
      },
      {
        title: 'Cuenta corriente',
        intro: 'Esta parte se ve cuando «Cliente» está marcado.',
        fields: [
          {
            label: 'Cuenta corriente cliente activa',
            description: 'Marcá esta opción si el cliente puede comprar y pagar después, en cuenta corriente.',
          },
          {
            label: 'Límite CC cliente',
            description: 'Cuánto puede deber como máximo. Podés dejarlo vacío si no querés poner un tope.',
          },
          {
            label: 'Moneda CC cliente',
            description: 'En qué moneda se controla el límite y la deuda (pesos, dólares, etc.).',
          },
        ],
      },
      {
        title: 'Grupos',
        intro:
          'Acá podés marcar a qué grupos pertenece el cliente. Los grupos se arman antes en «Grupos de Clientes», del menú de la izquierda. Sirven para ordenar clientes, aplicar precios o hacer reportes.',
      },
      {
        title: 'Información de pago',
        intro:
          'Datos de la cuenta bancaria del cliente, por si necesitás transferirle o registrar cómo te paga. Podés agregar más de una cuenta con «Agregar info de pago».',
        fields: [
          {
            label: 'Banco',
            description: 'Nombre del banco.',
          },
          {
            label: 'Tipo de cuenta',
            description: 'Por ejemplo: caja de ahorro o cuenta corriente.',
          },
          {
            label: 'N° de cuenta',
            description: 'El número de la cuenta bancaria.',
          },
          {
            label: 'CBU',
            description: 'La clave bancaria para hacer transferencias.',
          },
          {
            label: 'Alias',
            description: 'El alias de la cuenta, si tiene.',
          },
          {
            label: 'Titular',
            description: 'Nombre de la persona a nombre de quien está la cuenta.',
          },
          {
            label: 'Identificación titular',
            description: 'CUIT o DNI del titular de la cuenta.',
          },
          {
            label: 'Observaciones',
            description: 'Cualquier nota extra sobre esa cuenta.',
          },
        ],
      },
      {
        title: 'Paso final — Guardar',
        intro:
          'Cuando terminaste, tocá «Crear entidad». El sistema pide al menos la razón social y que sea cliente o proveedor. Si falta algo o hay un error, te avisa. Si salió bien, ves un mensaje de confirmación y la ventana se cierra.',
      },
    ],
  },
}
